import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Image,
  Typography,
  Tag,
  Button,
  Spin,
  message,
  Descriptions,
  Divider,
  Space,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  ShoppingCartOutlined,
  HeartOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../../helper/apiClient";
import apiPath from "../../constants/apiPath";

const { Title, Text, Paragraph } = Typography;

const ProductView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  // Fetch product data
  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${apiPath.marketPlaceProductView}${id}/`,
      );

      console.log("Product data:", response.data);

      if (response.data?.success) {
        const productData = response.data.data || response.data.product;
        setProduct(productData);
        // Set default selected image as primary image
        if (productData.images && productData.images.length > 0) {
          const primaryImage =
            productData.images.find((img) => img.is_primary) ||
            productData.images[0];
          setSelectedImage(primaryImage);
        }
      } else {
        message.error(response.data?.message || "Failed to fetch product");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      message.error(error.response?.data?.message || "Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  // Handle edit product
  const handleEdit = () => {
    navigate(`/catalog/products/edit/${id}`);
  };

  // Handle back
  const handleBack = () => {
    navigate("/catalog/products");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Title level={3}>Product not found</Title>
        <Button onClick={handleBack}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="product-view-container">
      {/* Header */}
      <div className="product-view-header">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
        <Title level={3} style={{ margin: 0, textAlign: "center" }}>
          Product Details
        </Title>
        <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
          Edit Product
        </Button>
      </div>

      <Row gutter={24}>
        {/* Left Column - Images */}
        <Col xs={24} md={10}>
          <Card className="product-view-image-card">
            {/* Main Image */}
            <div className="main-image">
              {selectedImage ? (
                <img
                  src={selectedImage.image_url}
                  alt={selectedImage.alt_text || product.name}
                  style={{ width: "100%", height: "auto", borderRadius: 8 }}
                />
              ) : (
                <div className="no-image">No Image Available</div>
              )}
            </div>

            {/* Thumbnail Images */}
            {product.images && product.images.length > 1 && (
              <div className="thumbnail-images">
                {product.images.map((img) => (
                  <div
                    key={img.id}
                    className={`thumbnail ${selectedImage?.id === img.id ? "active" : ""}`}
                    onClick={() => setSelectedImage(img)}
                  >
                    <img
                      src={img.image_url}
                      alt={img.alt_text || product.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>

        {/* Right Column - Product Details */}
        <Col xs={24} md={14}>
          <Card className="product-view-info-card">
            <Title level={2}>{product.name}</Title>

            {/* Tags */}
            <Space size={8} wrap style={{ marginBottom: 16 }}>
              {product.featured && <Tag color="gold">Featured</Tag>}
              {product.bestseller && <Tag color="blue">Bestseller</Tag>}
              {product.limited_edition && (
                <Tag color="purple">Limited Edition</Tag>
              )}
              <Tag color={product.status ? "green" : "red"}>
                {product.status ? "Active" : "Inactive"}
              </Tag>
              {product.stock_quantity > 0 ? (
                <Tag color="green">
                  In Stock ({product.stock_quantity} available)
                </Tag>
              ) : (
                <Tag color="red">Out of Stock</Tag>
              )}
            </Space>

            {/* Price */}
            <div className="product-price">
              <Text type="secondary">Points:</Text>
              <Title level={2} style={{ color: "#ff4d4f", margin: 0 }}>
                {/* {product.price} */}
                {parseFloat(product.price)}
              </Title>
            </div>

            <Divider />

            {/* Product Details */}
            <Descriptions column={1} bordered>
              <Descriptions.Item label="SKU">
                {product.sku || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {product.category_name || "Uncategorized"}
              </Descriptions.Item>
              <Descriptions.Item label="Manufacturer">
                {product.manufacturer || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Stock Quantity">
                {product.stock_quantity}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {new Date(product.created_at).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {new Date(product.updated_at).toLocaleDateString()}
              </Descriptions.Item>
            </Descriptions>

            {/* Description */}
            {product.description && (
              <>
                <Divider />
                <Title level={4}>Description</Title>
                <Paragraph>{product.description}</Paragraph>
              </>
            )}

            {/* Attributes */}
            {product.attributes && product.attributes.length > 0 && (
              <>
                <Divider />
                <Title level={4}>Attributes</Title>
                <Row gutter={[16, 16]}>
                  {product.attributes.map((attr, index) => (
                    <Col span={12} key={index}>
                      <Card size="small">
                        <Text strong>{attr.name}:</Text> {attr.value}
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            {/* Specifications */}
            {product.specifications &&
              Object.keys(product.specifications).length > 0 && (
                <>
                  <Divider />
                  <Title level={4}>Specifications</Title>
                  <Descriptions column={1} bordered>
                    {Object.entries(product.specifications).map(
                      ([key, value]) => (
                        <Descriptions.Item key={key} label={key}>
                          {value}
                        </Descriptions.Item>
                      ),
                    )}
                  </Descriptions>
                </>
              )}

            {/* Action Buttons */}
            {/* <div className="product-actions">
                            <Button size="large" icon={<ShoppingCartOutlined />}>
                                Add to Cart
                            </Button>
                            <Button size="large" icon={<HeartOutlined />}>
                                Add to Wishlist
                            </Button>
                        </div> */}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProductView;
